import { cursorNextPageParam } from '@repo/sdk'
import {
	type V1UsersSearchUsersResponse,
	v1AppConfigGetLoginBranding,
	v1UsersGetMeStorage,
	v1UsersSearchUsers
} from '@repo/sdk/gen'
import { captureException } from '@sentry/react'
import type { InfiniteData, QueryClient } from '@tanstack/react-query'
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import { resolveSession } from '@/lib/session-resolver'
import { makeQueryKeys } from '@/query/query-keys'

/**
 * THE SESSION CONTRACT. The single interface every surface in this app reads
 * identity, current organization and role from.
 *
 * It is a DISCRIMINATED UNION on `sessionType`, not an object of optional
 * fields that have to co-vary (`architecture.md`). An authenticated session
 * without an organization is not a state this type can express, so no caller
 * has to check for it and no caller can forget to. That matters most in the
 * role guards: a `role` that could be undefined turns every guard into a
 * three-way branch, and the third branch is the one nobody tests.
 *
 * `staleTime` and `gcTime` stay Infinity so guards resolve from cache without
 * a network round trip mid-navigation.
 *
 * THE SUBSTITUTION. Replacing the MS2 mock with the real Keycloak read (T4.6,
 * OPSSTD-4691) touched one file, `lib/session-resolver.ts` — same signature,
 * same two arms. Three things would end that and a machine checks none of
 * them: re-implementing the resolver inline in a surface, returning
 * session-derived values from `beforeLoad`, and importing the resolver from
 * anywhere but here. `routing.md` holds the rules. It had one importer per app
 * when this was written, so grep it before estimating the next change.
 *
 * WHAT MUST NEVER COME BACK: anything the identity provider issues. No bearer
 * credential, no raw assertion out of the token (T4.1.3).
 */
export function clientSessionQueryOptions() {
	return queryOptions({
		/** Keep the CSRF token available while the HttpOnly session cookie is valid. */
		gcTime: Number.POSITIVE_INFINITY,
		/** Refetched only via auth invalidation (login/logout) or the 401 cache clear, never per navigation. */
		staleTime: Number.POSITIVE_INFINITY,
		queryKey: makeQueryKeys.auth.session(),
		queryFn: () => resolveSession()
	})
}

/** The union above, nameable without adding an importer to the resolver. */
export type ClientSession = Awaited<ReturnType<typeof resolveSession>>

/**
 * Colleagues matching a typed term, a page at a time.
 *
 * **The organization is not a parameter, and must never become one.** The
 * search is confined to the caller's own organization by the row-level
 * isolation policy the request runs under, so the contract offers no field to
 * name a different one. A filter added here would be a second, weaker
 * perimeter that a reader could mistake for the real one.
 *
 * **It answers who exists, and nothing about what they can see.** A result
 * carries a name, an email and an avatar — never a hint of which folders that
 * person already reaches, which would leak somebody else's tree into a search
 * box.
 *
 * **It sits at the app and not inside the file manager**, because it names the
 * `user` domain rather than the directory one. The sharing panel is its first
 * consumer, not its owner: changing a role (T7.5.2) and the organization
 * policies of MS10 ask the same question, and this is where they will look.
 *
 * An empty term is not a search. The endpoint requires at least one character,
 * so the caller keeps the query disabled until there is one — expressed here,
 * where the rule belongs, rather than at each call site.
 */
export function peopleSearchQueryOptions(q: string, includeSelf = false) {
	const term = q.trim()

	return infiniteQueryOptions({
		queryKey: makeQueryKeys.users.search(term, includeSelf),
		queryFn: ({ pageParam, signal }): Promise<V1UsersSearchUsersResponse> =>
			v1UsersSearchUsers({
				// Absent rather than empty: the contract takes no `q` to mean
				// «the whole directory», and an empty string is a term that
				// matches nothing.
				//
				// `includeSelf` spento per difetto, come sul server: chi cerca
				// persone quasi sempre sta riempiendo un selettore di
				// condivisione, e offrirti te stesso lì è rumore. Lo accende
				// il filtro dell'elenco, dove «solo i miei file» è la prima
				// cosa che chiunque prova (OPSSTD-8604).
				query: {
					cursor: pageParam ?? undefined,
					q: term || undefined,
					// Stringa e non booleano: il parametro è `z.stringbool()` sul
					// server, quindi nel contratto è una stringa, e il client
					// generato la vuole così.
					...(includeSelf ? { includeSelf: 'true' } : {})
				},
				signal
			}),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: cursorNextPageParam,
		select: selectSearchPages
	})
}

/**
 * Who a set of identifiers belongs to.
 *
 * **The question an address asks and a search cannot.** A listing filtered by
 * people carries identifiers and nothing else, so a link opened cold — or a
 * reload — has nobody's name to show. `q` is no help: the name is exactly what
 * is missing. `ids` answers it (OPSSTD-7197), and this is the one place that
 * asks.
 *
 * **One page and no more.** The route pages at 20 and takes up to 50
 * identifiers, so a filter naming more than twenty people leaves the rest
 * unnamed until they turn up in a search of their own. That is the pre-existing
 * fallback rather than a new hole, and the alternative — asking for page after
 * page until the set is covered — is the client draining pagination, which
 * `sdk.md` rules out. What is on screen is covered either way: the button names
 * one person, and a panel that opened on twenty picked rows is not a panel
 * anybody assembled by hand.
 *
 * Disabled with no identifiers, because there is no question to ask.
 */
export function peopleByIdsQueryOptions(ids: string[]) {
	return queryOptions({
		queryKey: makeQueryKeys.users.byIds(ids),
		queryFn: ({ signal }) =>
			v1UsersSearchUsers({ query: { ids, limit: 20 }, signal }),
		enabled: ids.length > 0,
		select: (page: V1UsersSearchUsersResponse) => page.data
	})
}

/**
 * The envelope of each page reduced to the page's own results, with the paging
 * structure left intact.
 *
 * Not a flattening: the async pickers walk `pages` themselves, so collapsing
 * them here would hand that component an array where it expects a paged shape.
 *
 * Declared at module scope rather than inside the factory, because a `select`
 * minted per render is re-run per render and this one depends on nothing but
 * its argument.
 */
function selectSearchPages(data: InfiniteData<V1UsersSearchUsersResponse>) {
	return {
		pageParams: data.pageParams,
		pages: data.pages.map((page) => page.data)
	}
}

/**
 * The branding current for this instance (read live from the instance bucket
 * by the Sync Server): product
 * name and marks (`LOGIN-BRANDING.md`, "Delivery"). Public, so it
 * needs no session and can be read before sign-in.
 *
 * The cache holds the `instance` block alone: `version` answers a
 * question about the request, not about the brand.
 *
 * STALE FOR SIXTY SECONDS, the endpoint's own `max-age`: a published change
 * reaches an open tab about as fast as it reaches the login page.
 *
 * A FAILED READ IS NOT CACHED AND NEVER BREAKS A PAGE. `retry: false` and the
 * three-second cap keep an unreachable Sync Server from holding the first
 * paint, and the throw stays in the query so nothing dehydrates a failure as
 * if it were an answer. The consumers draw nothing branded through
 * `resolveBrand`; the root loader reports the failure and swallows it.
 *
 * WARMED BY THE ROOT ROUTE'S LOADER, so the first render already has it. It is
 * still read with `useQuery` and not a suspense hook: the warm may have
 * failed, and then `data` is `undefined` and nothing branded is drawn.
 */
export function instanceBrandingQueryOptions() {
	return queryOptions({
		queryKey: makeQueryKeys.appConfig.loginBranding(),
		staleTime: INSTANCE_BRANDING_STALE_MS,
		retry: false,
		queryFn: async ({ signal }) => {
			const response = await v1AppConfigGetLoginBranding({
				signal: AbortSignal.any([
					signal,
					AbortSignal.timeout(INSTANCE_BRANDING_TIMEOUT_MS)
				])
			})
			return response.instance
		}
	})
}

const INSTANCE_BRANDING_STALE_MS = 60 * 1000
const INSTANCE_BRANDING_TIMEOUT_MS = 3000

/**
 * Puts the instance branding in the cache before a route renders, and never
 * lets that fail a page: a failed read is reported and the caller carries on
 * with nothing branded. Stale data is served at once and refreshed behind it.
 *
 * Called from the root loader, which is the one that works when the server can
 * reach the API (an SSR profile), and from the loader of every route that
 * renders in the browser alone — the private shell and the public pages. The
 * SPA shell is prerendered at build time with no API to read, so the browser
 * is the only place its first paint can get the runtime brand from.
 */
export function warmInstanceBranding(queryClient: QueryClient) {
	return queryClient
		.ensureQueryData({
			...instanceBrandingQueryOptions(),
			revalidateIfStale: true
		})
		.catch((error: unknown) => {
			captureException(error)
			return undefined
		})
}

/**
 * How much space the caller uses, their own limit and how much more they can
 * upload (`user/frontend.md`, "Showing how much space the person has").
 *
 * It moves with every file the person creates or deletes for good, in this tab
 * or anywhere else, and with their quota. The data plane and the Sync Server
 * say so on the directory channel with `storage.changed` (OPSSTD-11215), which
 * `directory-events.ts` turns into an invalidation of this key, so no write in
 * this tab invalidates it itself. It is also read again whenever the window
 * regains focus, which the app-wide default refuses: the one change nobody is
 * told about — a colleague filling an organization quota that bounds
 * `availableBytes` — and a feed that was down catch up there.
 */
export function myStorageQueryOptions() {
	return queryOptions({
		queryKey: makeQueryKeys.users.meStorage(),
		queryFn: async ({ signal }) => {
			const response = await v1UsersGetMeStorage({ signal })
			return response.data
		},
		refetchOnWindowFocus: true,
		staleTime: 0
	})
}
